/**
 * TEMP — Set `false` before shipping. When `true`, the gallery lists every product image
 * (no Apex combination filtering); the slide matching the current Apex selection still
 * moves to index 0 so the primary image updates.
 */
const BODY_MEDIA_DISABLE_APEX_FILTER = false

const COVERAGE_FRACTION = 0.6
/** Display is ~1/3 of the viewport; 300w covers ~2× for typical 36vw float on mobile. */
const FLOATING_THUMB_PX = 300
/** Matches Tailwind `max-lg` (1023px): floating image only below the `lg` breakpoint. */
const FLOATING_IMAGE_MQ = '(max-width: 1023px)'
/** Hide the floater this many px before the section bottom would align with the floater bottom (viewport Y). */
const EARLY_HIDE_BEFORE_BOTTOM_CROSS_PX = 28

function mediaRootSelector(sectionId) {
  return `[data-product-media-v2="${sectionId}"]`
}

/** @param {ParentNode} [root] */
function getSearchRoot(root) {
  return root && typeof root.querySelectorAll === 'function' ? root : document
}

function getBodyMediaState(sectionId) {
  const mediaEl = document.querySelector(mediaRootSelector(sectionId))
  const splide = mediaEl?._bodyProductMediaSplide
  const slides = splide?.Components?.Elements?.slides
  if (!splide || !Array.isArray(slides) || !slides.length) return null
  return { mediaEl, splide, slides }
}

function initFloatingProductImage(el) {
  if (el.dataset.floatingImageInit === 'true') return

  const sectionId = el.getAttribute('data-floating-product-image-section')
  if (!sectionId) return

  const productSection =
    document.getElementById(`MainProduct-${sectionId}`) ||
    document.querySelector(`[data-main-product-v2="${sectionId}"]`)
  if (!productSection) return

  el.dataset.floatingImageInit = 'true'

  const belowLgMq = window.matchMedia(FLOATING_IMAGE_MQ)
  let ticking = false
  let visible = false

  const threshold = () => window.innerWidth * COVERAGE_FRACTION

  const setVisible = (next) => {
    if (next === visible) return
    visible = next
    el.dataset.visible = next ? 'true' : 'false'
    el.setAttribute('aria-hidden', next ? 'false' : 'true')
    el.tabIndex = next ? 0 : -1
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  const handleActivate = (e) => {
    if (el.getAttribute('data-visible') !== 'true') return
    e.preventDefault()
    scrollToTop()
  }

  el.addEventListener('click', handleActivate)
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') handleActivate(e)
  })

  const update = () => {
    if (!belowLgMq.matches) {
      setVisible(false)
      return
    }
    const scrollY = window.scrollY || window.pageYOffset || 0
    const pastCoverage = scrollY >= threshold()

    const sectionRect = productSection.getBoundingClientRect()
    const floatRect = el.getBoundingClientRect()
    const vh = window.innerHeight
    const sectionOverlapsViewport = sectionRect.bottom > 0 && sectionRect.top < vh
    const nearFloaterBottom = sectionRect.bottom < floatRect.bottom + EARLY_HIDE_BEFORE_BOTTOM_CROSS_PX

    setVisible(pastCoverage && sectionOverlapsViewport && !nearFloaterBottom)
  }

  const onScroll = () => {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      ticking = false
      update()
    })
  }

  update()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', update)
  if (typeof belowLgMq.addEventListener === 'function') {
    belowLgMq.addEventListener('change', update)
  } else if (typeof belowLgMq.addListener === 'function') {
    belowLgMq.addListener(update)
  }

  scheduleFloatingThumbPreload(sectionId)
}

// --- Apex product options → featured image (Splide + floating thumbnail) ---

/**
 * Filenames: `<anything>__<model>-<quantity>-<stand>.<ext>` — match only the part after `__` (no extension);
 * Shopify may append `_…` after that segment.
 */
function extractApexFileKeyFromSrc(src) {
  if (!src) return null
  let pathname
  try {
    pathname = new URL(src, window.location.href).pathname
  } catch (e) {
    return null
  }
  const file = decodeURIComponent(pathname.split('/').pop() || '')
  const idx = file.indexOf('__')
  if (idx === -1) return null
  let rest = file.slice(idx + 2)
  const lastDot = rest.lastIndexOf('.')
  if (lastDot > 0) rest = rest.slice(0, lastDot)
  return rest || null
}

function fileKeyMatches(key, desiredKey) {
  if (!key || !desiredKey) return false
  if (key === desiredKey) return true
  return key.split('_')[0] === desiredKey
}

/**
 * All valid `model-quantity-stand` keys from visible Apex inputs, filtered by
 * `APEX_STANDS_BY_QUANTITY` (same rules as `updateApexStandAvailability`).
 * @param {ParentNode} apexRoot
 * @returns {string[]}
 */
function enumerateValidApexKeys(apexRoot) {
  if (!apexRoot) return []
  const models = [...apexRoot.querySelectorAll('input[data-apex-input="model"]')].filter(
    (e) => e instanceof HTMLInputElement
  )
  const quantities = [...apexRoot.querySelectorAll('input[data-apex-input="quantity"]')].filter(
    (e) => e instanceof HTMLInputElement
  )
  const stands = [...apexRoot.querySelectorAll('input[data-apex-input="stand"]')].filter(
    (e) => e instanceof HTMLInputElement
  )
  const keys = []
  for (const m of models) {
    for (const q of quantities) {
      const allowed = APEX_STANDS_BY_QUANTITY[q.value]
      if (!allowed) continue
      for (const s of stands) {
        if (!allowed.has(s.value)) continue
        keys.push([m.value, q.value, s.value].join('-'))
      }
    }
  }
  return keys
}

function getApexOptionValues(apexRoot) {
  const model = readCheckedApexInputValue(apexRoot, 'model')
  const quantity = getEffectiveApexQuantity(apexRoot)
  const stand = getEffectiveApexStand(apexRoot)
  if (model == null) return null
  return { model, quantity, stand, key: [model, quantity, stand].join('-') }
}

/** Mount options available per panel quantity (door = `bare`). */
const APEX_STANDS_BY_QUANTITY = {
  '1p': new Set(['bare', 'floor', 'universal']),
  '2p': new Set(['bare', 'universal', 'motorized']),
  '4p': new Set(['bare', 'motorized']),
}

const APEX_DEFAULT_QUANTITY = '1p'
const APEX_DEFAULT_STAND = 'bare'

/** @param {ParentNode} apexRoot @param {'model'|'quantity'|'stand'} inputType */
function readCheckedApexInputValue(apexRoot, inputType) {
  const input = apexRoot.querySelector(`[data-apex-input="${inputType}"]:checked`)
  return input instanceof HTMLInputElement ? input.value : null
}

/** Effective quantity for media/pricing when step 2 is not yet chosen. */
function getEffectiveApexQuantity(apexRoot) {
  return readCheckedApexInputValue(apexRoot, 'quantity') ?? APEX_DEFAULT_QUANTITY
}

/** Effective stand for media/pricing when step 3 is not yet chosen. */
function getEffectiveApexStand(apexRoot) {
  return readCheckedApexInputValue(apexRoot, 'stand') ?? APEX_DEFAULT_STAND
}

const APEX_OPTION_STEPS = ['model', 'quantity', 'stand']

const APEX_URL_PARAM_VARIANT = 'variant'
const APEX_URL_PARAM_QUANTITY = 'quantity'
const APEX_URL_PARAM_STAND = 'stand'

/**
 * @returns {boolean} True when the URL encodes Apex state (e.g. shared deep link from body PDP).
 * When true, we mark desktop accordion steps as selected/open so options don't look "unpicked"
 * until the user touches them. When false, the default `setupApexDesktopAccordion` stays.
 */
function hasApexUrlParams() {
  try {
    const sp = new URLSearchParams(window.location.search)
    return sp.has(APEX_URL_PARAM_VARIANT) || sp.has(APEX_URL_PARAM_QUANTITY) || sp.has(APEX_URL_PARAM_STAND)
  } catch {
    return false
  }
}

/**
 * After radios reflect URL/defaults, shows each fieldset on xl as selected+expanded
 * (matches post-interaction `advanceApexOptionStep` appearance).
 * @param {ParentNode} apexRoot
 */
function syncApexDesktopUIFromUrl(apexRoot) {
  if (typeof window !== 'undefined' && window.matchMedia && !window.matchMedia('(min-width: 1280px)').matches) {
    return
  }

  let hasUrlQuantity = false
  try {
    hasUrlQuantity = new URLSearchParams(window.location.search).has(APEX_URL_PARAM_QUANTITY)
  } catch {
    hasUrlQuantity = false
  }

  for (const step of APEX_OPTION_STEPS) {
    const stepEl = getApexStep(apexRoot, step)
    if (!(stepEl instanceof HTMLElement)) continue
    const $input = apexRoot.querySelector(`[data-apex-input="${step}"]:checked`)
    if (!$input) continue
    // Stand stays collapsed until quantity is chosen; `?quantity=` deep links count.
    if (step === 'stand' && !hasUrlQuantity) continue
    setApexStepSelected(apexRoot, step, true)
    setApexStepOpen(stepEl, true)
  }
}

function updateApexStandAvailability(apexRoot) {
  const qty = getEffectiveApexQuantity(apexRoot)
  const allowed = APEX_STANDS_BY_QUANTITY[qty]
  if (!allowed) return

  const standInputs = apexRoot.querySelectorAll('input[data-apex-input="stand"]')
  standInputs.forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.disabled = !allowed.has(input.value)
    }
  })

  // Don't auto-select a stand: the user must choose one explicitly. Only clear a
  // previously-checked stand if it's no longer valid for the new quantity.
  const selected = apexRoot.querySelector('input[data-apex-input="stand"]:checked')
  if (selected instanceof HTMLInputElement && (selected.disabled || !allowed.has(selected.value))) {
    selected.checked = false
  }
}

/**
 * "Popular" badge moves to floor (1p), universal (2p), or motorized (4p).
 * @param {ParentNode} apexRoot
 */
function updateApexPopularBadge(apexRoot) {
  const qty = getEffectiveApexQuantity(apexRoot)
  apexRoot.querySelectorAll('[data-apex-stand-popular-badge]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return
    const forQty = el.dataset.apexPopularQuantity
    if (forQty == null) return
    el.classList.toggle('hidden', forQty !== qty)
  })
}

/**
 * Stand thumbnails ship as paired white/black art (Pro vs Elite image edges). Shows the
 * `img[data-apex-option-content-image="<model value>"]` that matches `model`; hides the other.
 * @param {ParentNode} apexRoot
 */
function syncApexStandThumbnailImagesForModel(apexRoot) {
  const model = apexRoot.querySelector('[data-apex-input="model"]:checked')?.value
  if (model !== '1500' && model !== '2160') return

  apexRoot.querySelectorAll('img[data-apex-option-content-image]').forEach((node) => {
    if (!(node instanceof HTMLImageElement)) return
    const variant = node.getAttribute('data-apex-option-content-image')
    if (!variant || (variant !== '1500' && variant !== '2160')) return
    node.classList.toggle('hidden', variant !== model)
  })
}

function getApexStep(apexRoot, step) {
  return apexRoot.querySelector(`[data-apex-group="${step}"]`)
}

function setApexStepOpen(stepEl, isOpen) {
  if (!(stepEl instanceof HTMLElement)) return
  stepEl.dataset.apexOpen = isOpen ? 'true' : 'false'
  const trigger = stepEl.querySelector('[data-apex-accordion-trigger]')
  if (trigger instanceof HTMLElement) {
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
  }
}

function setApexStepSelected(apexRoot, step, isSelected = true) {
  const stepEl = getApexStep(apexRoot, step)
  if (stepEl instanceof HTMLElement) {
    stepEl.dataset.apexSelected = isSelected ? 'true' : 'false'
  }
}

function openApexStep(apexRoot, step) {
  setApexStepOpen(getApexStep(apexRoot, step), true)
}

function setupApexDesktopAccordion(apexRoot) {
  APEX_OPTION_STEPS.forEach((step) => {
    const stepEl = getApexStep(apexRoot, step)
    if (!(stepEl instanceof HTMLElement)) return
    const startOpen = step === 'model' || step === 'quantity'
    setApexStepOpen(stepEl, startOpen)
    stepEl.dataset.apexSelected = 'false'

    const trigger = stepEl.querySelector('[data-apex-accordion-trigger]')
    if (trigger instanceof HTMLElement && trigger.dataset.apexAccordionTriggerInit !== 'true') {
      trigger.dataset.apexAccordionTriggerInit = 'true'
      trigger.addEventListener('click', () => {
        setApexStepOpen(stepEl, stepEl.dataset.apexOpen !== 'true')
      })
    }
  })
}

function advanceApexOptionStep(apexRoot, step) {
  setApexStepSelected(apexRoot, step)
  if (step === 'model') {
    openApexStep(apexRoot, 'quantity')
  } else if (step === 'quantity') {
    openApexStep(apexRoot, 'stand')
  }
}

function getSlideImageLikeSrc(slideEl) {
  const img = slideEl.querySelector('img')
  if (img) return img.currentSrc || img.src || ''
  return slideEl.querySelector('video')?.poster || ''
}

/**
 * Shrink a Shopify CDN image URL to the floating thumb size (avoid loading full gallery `width=`).
 * @param {string} src
 * @returns {string}
 */
function shopifyImageUrlForFloatingThumb(src) {
  if (!src) return src
  try {
    const u = new URL(src, window.location.href)
    u.searchParams.set('width', String(FLOATING_THUMB_PX))
    u.searchParams.set('height', String(FLOATING_THUMB_PX))
    u.searchParams.set('crop', 'center')
    return u.toString()
  } catch (e) {
    return src
  }
}

/**
 * `splide.Components.Elements.slides[i]` is the slide root `Element` in this theme
 * (see product-media-v2). Some Splide builds use `{ slide: Element }` instead.
 */
function splideSlideRoot(item) {
  if (!item) return null
  if (item.nodeType === 1) return item
  return item.slide || null
}

function findSlideIndexForApexKey(slides, desiredKey) {
  for (let i = 0; i < slides.length; i++) {
    const el = splideSlideRoot(slides[i])
    if (!el) continue
    const fileKey = extractApexFileKeyFromSrc(getSlideImageLikeSrc(el))
    if (fileKeyMatches(fileKey, desiredKey)) return i
  }
  return -1
}

function findSplideIndexForSlideRoot(splide, slideRoot) {
  const slides = splide?.Components?.Elements?.slides
  if (!Array.isArray(slides) || !slideRoot) return -1
  for (let i = 0; i < slides.length; i++) {
    if (splideSlideRoot(slides[i]) === slideRoot) return i
  }
  return -1
}

function getApexKeyForSlide(slideEl) {
  if (slideEl && slideEl._apexFileKey !== undefined) return slideEl._apexFileKey
  return extractApexFileKeyFromSrc(getSlideImageLikeSrc(slideEl))
}

function snapshotSlideImage(slideEl) {
  if (!slideEl || slideEl._apexImageSnapshot !== undefined) return
  const img = slideEl.querySelector('img')
  if (!(img instanceof HTMLImageElement)) {
    slideEl._apexImageSnapshot = null
    return
  }
  slideEl._apexImageSnapshot = {
    src: img.getAttribute('src') || '',
    srcset: img.getAttribute('srcset') || '',
    sizes: img.getAttribute('sizes') || '',
    alt: img.getAttribute('alt') || '',
  }
}

function applyImageSnapshot(img, snapshot) {
  if (!(img instanceof HTMLImageElement) || !snapshot?.src) return false
  img.src = snapshot.src
  if (snapshot.srcset) img.srcset = snapshot.srcset
  else img.removeAttribute('srcset')
  if (snapshot.sizes) img.sizes = snapshot.sizes
  else img.removeAttribute('sizes')
  if (snapshot.alt) img.alt = snapshot.alt
  else img.removeAttribute('alt')
  return true
}

function restoreSlideImageSnapshot(slideEl) {
  if (!slideEl?._apexImageSnapshot) return
  applyImageSnapshot(slideEl.querySelector('img'), slideEl._apexImageSnapshot)
}

function rememberOriginalBodyMedia(mediaEl, slides) {
  if (mediaEl._bodyProductOriginalSlides) return
  mediaEl._bodyProductOriginalSlides = slides
    .map((slide) => {
      const el = splideSlideRoot(slide)
      if (!el) return null
      el._apexFileKey = extractApexFileKeyFromSrc(getSlideImageLikeSrc(el))
      snapshotSlideImage(el)
      return el
    })
    .filter(Boolean)
  const thumbsList = mediaEl.querySelector('[data-product-media-v2-thumbs-list]')
  mediaEl._bodyProductOriginalThumbs = thumbsList
    ? [...thumbsList.querySelectorAll(':scope > .splide__slide')]
    : Array.from(mediaEl.querySelectorAll('[data-product-media-v2-thumb]'))
  mediaEl._bodyProductOriginalThumbs.forEach((thumbSlide) => snapshotSlideImage(thumbSlide))
}

function getApexHeroSlideEl(mediaEl, placement) {
  const list = mediaEl.querySelector('[data-product-media-v2-main] .splide__list')
  if (!list) return null
  const seeItSlide =
    placement === 'after-video'
      ? mediaEl._bodyProductOriginalSlides?.find((slide) => slide.hasAttribute('data-see-it-in-action-main-slide'))
      : null
  if (seeItSlide && list.contains(seeItSlide)) return list.children[1] || null
  return list.children[0] || null
}

function copyThumbVisual(mediaEl, heroSlide, selectedSlide) {
  const thumbsList = mediaEl.querySelector('[data-product-media-v2-thumbs-list]')
  if (!thumbsList || !mediaEl._bodyProductOriginalThumbs) return
  const heroIndex = heroSlide.getAttribute('data-body-slide-index')
  const selectedIndex = selectedSlide.getAttribute('data-body-slide-index')
  const heroThumb =
    heroIndex != null
      ? thumbsList.querySelector(`:scope > [data-body-slide-index="${CSS.escape(heroIndex)}"]`)
      : null
  const sourceThumb = mediaEl._bodyProductOriginalThumbs.find(
    (thumb) => thumb.getAttribute('data-body-slide-index') === selectedIndex
  )
  if (!(heroThumb instanceof HTMLElement) || !sourceThumb?._apexImageSnapshot) return
  applyImageSnapshot(heroThumb.querySelector('img'), sourceThumb._apexImageSnapshot)
}

/**
 * Quantity/stand changes only swap the featured Apex still. Avoids `splide.refresh()`
 * on consecutive clicks, which is what made the 2nd/3rd quantity tap feel stuck.
 * @returns {Element | null} in-list slide to navigate to, or null to fall back to rebuild
 */
function trySwapApexHero(state, selectedKey, placement) {
  const { mediaEl } = state
  const list = mediaEl.querySelector('[data-product-media-v2-main] .splide__list')
  const originalSlides = mediaEl._bodyProductOriginalSlides
  if (!list || !originalSlides) return null

  const selectedSlide = originalSlides.find((slide) => fileKeyMatches(getApexKeyForSlide(slide), selectedKey))
  if (!selectedSlide?._apexImageSnapshot) return null

  const heroSlide = list.contains(selectedSlide) ? selectedSlide : getApexHeroSlideEl(mediaEl, placement)
  if (!(heroSlide instanceof HTMLElement) || heroSlide.querySelector('video')) return null
  const heroImg = heroSlide.querySelector('img')
  if (!applyImageSnapshot(heroImg, selectedSlide._apexImageSnapshot)) return null
  // Outrank the idle warm-up queue and any remaining lazy gallery images.
  if (heroImg instanceof HTMLImageElement) heroImg.fetchPriority = 'high'
  copyThumbVisual(mediaEl, heroSlide, selectedSlide)
  return heroSlide
}

function rebuildBodyMediaOrder(state, selectedKey, validKeys, placement = 'first') {
  const { mediaEl, splide, slides } = state
  const list = mediaEl.querySelector('[data-product-media-v2-main] .splide__list')
  const thumbsList = mediaEl.querySelector('[data-product-media-v2-thumbs-list]')
  if (!list) return null

  rememberOriginalBodyMedia(mediaEl, slides)

  const originalSlides = mediaEl._bodyProductOriginalSlides || []
  originalSlides.forEach(restoreSlideImageSnapshot)
  mediaEl._bodyProductOriginalThumbs?.forEach(restoreSlideImageSnapshot)
  const selectedSlide = originalSlides.find((slide) => fileKeyMatches(getApexKeyForSlide(slide), selectedKey))
  if (!selectedSlide) return null

  const seeItSlide =
    placement === 'after-video'
      ? originalSlides.find((slide) => slide.hasAttribute('data-see-it-in-action-main-slide'))
      : null
  const leadingSlides = seeItSlide ? [seeItSlide, selectedSlide] : [selectedSlide]

  let orderedSlides
  if (BODY_MEDIA_DISABLE_APEX_FILTER) {
    orderedSlides = [...leadingSlides, ...originalSlides.filter((slide) => !leadingSlides.includes(slide))]
  } else {
    const apexKeys = new Set(validKeys)
    orderedSlides = [
      ...leadingSlides,
      ...originalSlides.filter((slide) => {
        if (leadingSlides.includes(slide)) return false
        const apexKey = getApexKeyForSlide(slide)
        return !apexKey || !apexKeys.has(apexKey.split('_')[0])
      }),
    ]
  }

  list.replaceChildren(...orderedSlides)

  if (thumbsList && mediaEl._bodyProductOriginalThumbs) {
    const originalThumbs = mediaEl._bodyProductOriginalThumbs

    const orderedThumbs = (() => {
      const useStableIndex =
        orderedSlides.every((el) => el && el.hasAttribute && el.hasAttribute('data-body-slide-index')) &&
        originalThumbs.every((el) => el && el.hasAttribute && el.hasAttribute('data-body-slide-index'))

      if (useStableIndex) {
        return orderedSlides
          .map((slideEl) => {
            const stable = slideEl.getAttribute('data-body-slide-index')
            if (stable == null) return null
            return originalThumbs.find((thumbSlide) => thumbSlide.getAttribute('data-body-slide-index') === stable)
          })
          .filter(Boolean)
      }

      return orderedSlides
        .map((slide) => originalSlides.indexOf(slide))
        .map((originalIndex) => originalThumbs[originalIndex])
        .filter(Boolean)
    })()

    orderedThumbs.forEach((thumbSlide, index) => {
      const btn = thumbSlide.querySelector('[data-product-media-v2-thumb]')
      if (btn) btn.dataset.slideIndex = String(index)
    })
    if (orderedThumbs.length === orderedSlides.length) {
      thumbsList.replaceChildren(...orderedThumbs)
    }
  }

  splide.refresh()
  try {
    mediaEl._bodyProductThumbSplide?.refresh?.()
  } catch (e) {}
  mediaEl._applyProductGalleryRoles?.()
  return selectedSlide
}

/**
 * 300×300 URLs for every floating thumb that might be shown. Uses valid Apex
 * combinations when `[data-apex-product-options]` exists; if none match media
 * filenames, falls back to all gallery slides.
 * @param {string} sectionId
 * @param {ParentNode | null} apexRoot
 * @returns {string[]}
 */
function collectFloatingThumbUrlsToPreload(sectionId, apexRoot) {
  const state = getBodyMediaState(sectionId)
  if (!state) return []
  const { slides } = state
  const urls = new Set()

  if (apexRoot) {
    for (const key of enumerateValidApexKeys(apexRoot)) {
      const i = findSlideIndexForApexKey(slides, key)
      if (i < 0) continue
      const slideEl = splideSlideRoot(slides[i])
      if (!slideEl) continue
      const raw = getSlideImageLikeSrc(slideEl)
      if (raw) urls.add(shopifyImageUrlForFloatingThumb(raw))
    }
  }

  if (urls.size === 0) {
    for (let i = 0; i < slides.length; i++) {
      const slideEl = splideSlideRoot(slides[i])
      if (!slideEl) continue
      const raw = getSlideImageLikeSrc(slideEl)
      if (raw) urls.add(shopifyImageUrlForFloatingThumb(raw))
    }
  }
  return [...urls]
}

function runFloatingThumbPreload(urls) {
  for (const url of urls) {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
  }
}

/**
 * After Splide is ready, preloads 300×300 hover-thumb assets (idle time, non-blocking).
 * @param {string} sectionId
 */
function scheduleFloatingThumbPreload(sectionId) {
  const floatEl = document.querySelector(
    `[data-floating-product-image][data-floating-product-image-section="${sectionId}"]`
  )
  if (!floatEl || floatEl.dataset.floatingThumbsPreloaded === 'true') return

  const apexRoot = document.querySelector(`[data-apex-product-options][data-section-id="${sectionId}"]`)

  whenSplideReady(sectionId, () => {
    if (floatEl.dataset.floatingThumbsPreloaded === 'true') return
    const urls = collectFloatingThumbUrlsToPreload(sectionId, apexRoot)
    if (!urls.length) return
    floatEl.dataset.floatingThumbsPreloaded = 'true'
    const kick = () => runFloatingThumbPreload(urls)
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(kick, { timeout: 4000 })
    } else {
      setTimeout(kick, 0)
    }
  })
}

/** Metered / very slow connections opt out of speculative gallery fetching. */
function shouldSkipApexImagePreload() {
  const conn = navigator.connection
  if (!conn) return false
  return Boolean(conn.saveData) || /(^|-)2g$/.test(conn.effectiveType || '')
}

/** Fetches the same srcset candidate the gallery would pick, so a later swap is a cache hit. */
function warmSlideImage(slideEl) {
  const img = slideEl?.querySelector('img')
  if (!(img instanceof HTMLImageElement)) return
  const src = img.getAttribute('src')
  if (!src) return
  const warmer = new Image()
  warmer.decoding = 'async'
  warmer.fetchPriority = 'low'
  // `sizes` must be set before `srcset` for candidate selection to use it.
  const sizes = img.getAttribute('sizes')
  const srcset = img.getAttribute('srcset')
  if (sizes) warmer.sizes = sizes
  if (srcset) warmer.srcset = srcset
  warmer.src = src
}

function collectApexSlidesToWarm(state, apexRoot) {
  const validKeys = new Set(enumerateValidApexKeys(apexRoot))
  const model = readCheckedApexInputValue(apexRoot, 'model')
  const source =
    state.mediaEl._bodyProductOriginalSlides || state.slides.map((slide) => splideSlideRoot(slide)).filter(Boolean)

  const currentModel = []
  const otherModels = []
  for (const slideEl of source) {
    const key = getApexKeyForSlide(slideEl)
    if (!key) continue
    const base = key.split('_')[0]
    if (!validKeys.has(base)) continue
    if (model && base.startsWith(`${model}-`)) currentModel.push(slideEl)
    else otherModels.push(slideEl)
  }
  return [...currentModel, ...otherModels]
}

function onIdle(callback) {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(callback, { timeout: 3000 })
  else setTimeout(callback, 0)
}

/** Keeps speculative fetches from competing with the initial page load. */
function afterWindowLoad(callback) {
  if (document.readyState === 'complete') callback()
  else window.addEventListener('load', callback, { once: true })
}

/**
 * Apex stills past the first two slides render `loading="lazy"`, so the first switch to a
 * combination blocked on a full-size fetch. Warms them a few at a time during idle,
 * selected model first.
 * @param {ParentNode} apexRoot
 * @param {string} sectionId
 */
function scheduleApexMainImagePreload(apexRoot, sectionId) {
  if (shouldSkipApexImagePreload()) return

  whenSplideReady(sectionId, () => {
    const state = getBodyMediaState(sectionId)
    if (!state || state.mediaEl.dataset.apexMainImagesPreloaded === 'true') return

    const queue = collectApexSlidesToWarm(state, apexRoot)
    if (!queue.length) return
    state.mediaEl.dataset.apexMainImagesPreloaded = 'true'

    const drain = () => {
      queue.splice(0, 3).forEach(warmSlideImage)
      if (queue.length) onIdle(drain)
    }
    afterWindowLoad(() => onIdle(drain))
  })
}

function updateFloatingImageFromSlide(slideEl, floatContainer) {
  const floatImg = floatContainer?.querySelector('img')
  if (!floatImg) return
  const src = getSlideImageLikeSrc(slideEl)
  if (!src) return
  const sized = shopifyImageUrlForFloatingThumb(src)
  floatImg.removeAttribute('srcset')
  floatImg.width = FLOATING_THUMB_PX
  floatImg.height = FLOATING_THUMB_PX
  floatImg.src = sized
}

function syncApexToMedia(apexRoot, sectionId, placement = 'first', goToSelected = true) {
  const values = getApexOptionValues(apexRoot)
  if (!values) return

  const state = getBodyMediaState(sectionId)
  if (!state) return

  const syncKey = `${values.key}|${placement}|${goToSelected}`
  if (state.mediaEl._apexMediaSyncKey === syncKey) return

  const { splide, mediaEl } = state
  const orderKey = `${values.key}|${placement}`
  let selectedSlide = null
  if (mediaEl._apexMediaOrderKey === orderKey) {
    selectedSlide = getApexHeroSlideEl(mediaEl, placement)
  } else {
    selectedSlide = trySwapApexHero(state, values.key, placement)
  }
  if (!selectedSlide) {
    selectedSlide = rebuildBodyMediaOrder(state, values.key, enumerateValidApexKeys(apexRoot), placement)
  }
  if (!selectedSlide) return
  mediaEl._apexMediaSyncKey = syncKey
  mediaEl._apexMediaOrderKey = orderKey

  const navigate = () => {
    const selectedIndex = findSplideIndexForSlideRoot(splide, selectedSlide)
    const targetIndex = goToSelected && selectedIndex >= 0 ? selectedIndex : 0
    try {
      splide.go(targetIndex)
    } catch (e) {
      return
    }

    const floatContainer = document.querySelector(
      `[data-floating-product-image][data-floating-product-image-section="${sectionId}"]`
    )
    if (floatContainer) updateFloatingImageFromSlide(selectedSlide, floatContainer)
  }

  requestAnimationFrame(navigate)
}

const pendingApexMediaSync = new Map()

function scheduleApexMediaSync(apexRoot, placement = 'first', goToSelected = true) {
  const sectionId = apexRoot.getAttribute('data-section-id')
  if (!sectionId) return
  const existing = pendingApexMediaSync.get(sectionId)
  if (existing != null) cancelAnimationFrame(existing)
  const id = requestAnimationFrame(() => {
    pendingApexMediaSync.delete(sectionId)
    whenSplideReady(sectionId, () => syncApexToMedia(apexRoot, sectionId, placement, goToSelected))
  })
  pendingApexMediaSync.set(sectionId, id)
}

const splideReadyWaiters = new Map()

if (!window.__bodyProductMediaReadyBound) {
  window.__bodyProductMediaReadyBound = true
  document.addEventListener('body-product-media-ready', (event) => {
    const sectionId = String(event.detail?.sectionId ?? '')
    const waiters = splideReadyWaiters.get(sectionId)
    if (!waiters?.length) return
    splideReadyWaiters.delete(sectionId)
    waiters.forEach((cb) => cb())
  })
}

function whenSplideReady(sectionId, callback, attempt = 0) {
  const key = String(sectionId)
  if (document.querySelector(mediaRootSelector(key))?._bodyProductMediaSplide) {
    callback()
    return
  }
  if (attempt === 0) {
    if (!splideReadyWaiters.has(key)) splideReadyWaiters.set(key, [])
    splideReadyWaiters.get(key).push(callback)
  }
  if (attempt > 100) return
  requestAnimationFrame(() => whenSplideReady(key, callback, attempt + 1))
}

function initApexProductOptions(apexRoot) {
  if (apexRoot.dataset.apexProductOptionsInit === 'true') return
  const sectionId = apexRoot.getAttribute('data-section-id')
  if (!sectionId) return

  apexRoot.dataset.apexProductOptionsInit = 'true'

  const onApexInputChange = (inputType) => {
    if (inputType === 'quantity' || inputType === 'model') {
      updateApexStandAvailability(apexRoot)
      updateApexPopularBadge(apexRoot)
    }
    if (inputType === 'model') {
      syncApexStandThumbnailImagesForModel(apexRoot)
    }
  }

  setupApexDesktopAccordion(apexRoot)

  apexRoot.addEventListener('change', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.hasAttribute('data-apex-input')) {
      advanceApexOptionStep(apexRoot, e.target.dataset.apexInput)
      onApexInputChange(e.target.dataset.apexInput)
    }
  })

  apexRoot.addEventListener('click', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.hasAttribute('data-apex-input')) {
      advanceApexOptionStep(apexRoot, e.target.dataset.apexInput)
    }
  })

  updateApexStandAvailability(apexRoot)
  updateApexPopularBadge(apexRoot)
  if (hasApexUrlParams()) {
    syncApexDesktopUIFromUrl(apexRoot)
  }
  syncApexStandThumbnailImagesForModel(apexRoot)
  scheduleApexMediaSync(apexRoot, 'after-video', false)
  scheduleApexMainImagePreload(apexRoot, sectionId)
}

function initApexProductOptionsInScope(root) {
  getSearchRoot(root)
    .querySelectorAll('[data-apex-product-options]')
    .forEach((node) => {
      if (node instanceof HTMLElement) initApexProductOptions(node)
    })
}

function initFloatingInScope(root) {
  getSearchRoot(root)
    .querySelectorAll('[data-floating-product-image][data-floating-product-image-section]')
    .forEach((node) => {
      if (node instanceof HTMLElement) initFloatingProductImage(node)
    })
}

function runInit() {
  initFloatingInScope(document)
  initApexProductOptionsInScope(document)
}

if (!window.__apexSelectionMediaSyncBound) {
  window.__apexSelectionMediaSyncBound = true
  window.addEventListener('apex-selection-update', () => {
    document.querySelectorAll('[data-apex-product-options]').forEach((node) => {
      if (node instanceof HTMLElement) scheduleApexMediaSync(node, 'after-video', true)
    })
  })
}

if (!window.__mainProductBodyJsBound) {
  window.__mainProductBodyJsBound = true
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit)
  } else {
    runInit()
  }
  document.addEventListener('shopify:section:load', (e) => {
    if (e?.target instanceof HTMLElement) {
      initFloatingInScope(e.target)
      initApexProductOptionsInScope(e.target)
    }
  })
} else {
  runInit()
}
